import { featureRegistry } from './feature-registry';
import { hasMutationObserver, requestFrame, type FrameHandle } from '../shared/platform';

function hasMatch(selector: string, root: ParentNode): boolean {
  if (root instanceof Element && root.matches(selector)) {
    return true;
  }

  return root.querySelector(selector) !== null;
}

export function scanForFeatures(root: ParentNode = document): void {
  featureRegistry.forEach((feature) => {
    if (!hasMatch(feature.selector, root)) {
      return;
    }

    void feature.load().then((initialize) => {
      initialize(root);
    });
  });
}

export function observeDynamicFragments(): MutationObserver | null {
  if (!hasMutationObserver() || document.body === null) {
    return null;
  }

  const pendingRoots = new Set<ParentNode>();
  let frame: FrameHandle | null = null;

  const flush = (): void => {
    frame = null;
    pendingRoots.forEach((root) => {
      scanForFeatures(root);
    });
    pendingRoots.clear();
  };

  const schedule = (root: ParentNode): void => {
    pendingRoots.add(root);

    if (frame !== null) {
      return;
    }

    frame = requestFrame(() => {
      flush();
    });
  };

  const observer = new MutationObserver((entries) => {
    entries.forEach((entry) => {
      entry.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) {
          return;
        }

        schedule(node);
      });
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  return observer;
}

export function exposeFragmentRuntimeApi(): void {
  const globalScope = window as typeof window & {
    FragmentRuntime?: {
      scan: (root?: ParentNode) => void;
    };
  };

  globalScope.FragmentRuntime = {
    scan: (root?: ParentNode) => {
      scanForFeatures(root ?? document);
    },
  };
}
